import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';

import {
  Save,
  FolderOpen,
  Sheet,
  Download,
  Upload,
  Plus,
  Calculator,
  BarChart3,
  Sparkles,
  ChevronDown,
  History,
  Trash2
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu-custom';
import { ContextMenu, useContextMenu, type ContextMenuItem } from '../ui/context-menu-custom';

import { globalVFS } from '../../utils/storage/VFS';
import { useToast } from '../../hooks/use-toast';

import { saveAs } from 'file-saver';
import MapleSheetChartModal from './MapleSheetChartModal';
import ChartOverlay, { ChartOverlayItem, ChartOverlayConfig } from './ChartOverlay';
import { supabase } from '../../integrations/supabase/client';
import { useAuth } from '../../hooks/useAuth';
import { DraggableFileDialog } from '../DraggableFileDialog';
import { ensureDir } from '../../utils/filesystem';
import { evaluateFormula as evaluateExcelFormula } from '../../utils/excelFormulas';

import FormulaSuggestions from './FormulaSuggestions';
import { EXCEL_FUNCTIONS } from '../../utils/excelFunctions';

import FormulaLibraryDialog from './FormulaLibraryDialog';
import MapleSheetStatusBar from './MapleSheetStatusBar';
import MapleSheetAiToolbar from './MapleSheetAiToolbar';
import ShortcutRunDialog from './shortcut/ShortcutRunDialog';
import ShortcutRunStatus from './shortcut/ShortcutRunStatus';
import ShortcutReviewDialog from './shortcut/ShortcutReviewDialog';
import ShortcutHistoryDialog from './shortcut/ShortcutHistoryDialog';
import type { ShortcutRunDialogSubmission, ShortcutRunRecord, ShortcutRunReviewData } from './shortcut/types';
import { isShortcutTerminalStatus } from './shortcut/types';
import { buildMapleSheetCsvExport, buildMapleSheetDocumentContent, buildMapleSheetWorkbookExport, columnIndexToLetter, rangeBoundsToLabel, tableToSpreadsheetData } from './shortcut/utils';

import { buildShortcutPrompt, createShortcutRunRecord, downloadShortcutArtifact, downloadShortcutResult, getShortcutSourceTypeFromName, listShortcutRunHistory, markShortcutRunApplied, parseShortcutReviewData, saveShortcutFile, submitShortcutJob, syncShortcutRunStatus, uploadShortcutFile, verifyShortcutConnection } from '@/lib/shortcut';
import useOsDark from '../../hooks/useOsDark';

interface Cell {
  value: string;
  formula?: string;
  style?: {
    bold?: boolean;
    italic?: boolean;
    backgroundColor?: string;
    textColor?: string;
    fontSize?: number;
  };
}

interface SpreadsheetData {
  [key: string]: Cell;
}

interface MapleSheetProps {
  filePath?: string;
  initialContent?: string;
}

export const MapleSheet: React.FC<MapleSheetProps> = ({ filePath, initialContent }) => {
  const [data, setData] = useState<SpreadsheetData>({} as SpreadsheetData);
  const [selectedCell, setSelectedCell] = useState('A1');
  const [formulaInput, setFormulaInput] = useState('');
  const [sheetName, setSheetName] = useState('Untitled Spreadsheet');
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [columnCount, setColumnCount] = useState(DEFAULT_COLUMN_COUNT);
  const containerRef = useRef<HTMLDivElement>(null);
  const isDark = useOsDark();
  const lightSheetCardStyle = isDark ? undefined : { backgroundColor: '#ffffff', borderColor: '#cbd5e1' };
  const lightSheetTableStyle = isDark ? undefined : { backgroundColor: '#ffffff' };
  const lightSheetHeaderStyle = isDark ? undefined : { backgroundColor: '#f8fafc', borderColor: '#cbd5e1', color: '#1e293b' };
  const lightSheetRowHeaderStyle = isDark ? undefined : { backgroundColor: '#f8fafc', borderColor: '#cbd5e1', color: '#334155' };
  const getLightGridCellStyle = (isSelected: boolean, inDragSelect: boolean) => {
    if (isDark) return undefined;

    return {
      backgroundColor: isSelected ? '#e0f2fe' : inDragSelect ? '#f0f9ff' : '#ffffff',
      borderColor: isSelected ? '#0ea5e9' : '#cbd5e1',
      boxShadow: inDragSelect ? 'inset 0 0 0 1px #38bdf8' : undefined,
    };
  };
  const sheetCardClassName = isDark
    ? 'maplesheet-grid-surface min-h-full relative bg-card'
    : 'maplesheet-grid-surface min-h-full relative border-slate-300 bg-white';
  const sheetHeaderCellClassName = isDark
    ? 'maplesheet-grid-header border border-border w-24 h-8 bg-muted text-xs font-medium sticky top-0 z-30'
    : 'maplesheet-grid-header border border-slate-300 w-24 h-8 bg-slate-100 text-slate-800 text-xs font-medium sticky top-0 z-30';
  const sheetCornerCellClassName = isDark
    ? 'maplesheet-grid-corner border border-border w-12 h-8 bg-muted text-xs sticky left-0 top-0 z-40'
    : 'maplesheet-grid-corner border border-slate-300 w-12 h-8 bg-slate-100 text-xs sticky left-0 top-0 z-40';
  const sheetRowHeaderClassName = isDark
    ? 'maplesheet-grid-row-header border border-border w-12 h-8 bg-muted text-center text-xs font-medium sticky left-0 z-10'
    : 'maplesheet-grid-row-header border border-slate-300 w-12 h-8 bg-slate-50 text-slate-700 text-center text-xs font-medium sticky left-0 z-10';
  const getGridCellClassName = (isSelected: boolean, inDragSelect: boolean) => {
    if (isDark) {
      return `maplesheet-grid-cell border border-border w-24 h-8 p-0 cursor-pointer hover:bg-muted/50 ${
        isSelected ? 'maplesheet-grid-cell-selected bg-primary/20 border-primary focus-within:ring-2 focus-within:ring-primary/60' : ''
      } ${inDragSelect ? 'maplesheet-grid-cell-range bg-primary/20 ring-1 ring-primary' : ''}`;
    }

    return `maplesheet-grid-cell border border-slate-300 bg-white w-24 h-8 p-0 cursor-pointer hover:bg-slate-50 ${
      isSelected ? 'maplesheet-grid-cell-selected bg-sky-100 border-sky-500 focus-within:ring-2 focus-within:ring-sky-400/60' : ''
    } ${inDragSelect ? 'maplesheet-grid-cell-range bg-sky-50 ring-1 ring-sky-400' : ''}`;
  };
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);
  
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    const onResize = () => setViewportHeight(el.clientHeight);
    onResize();
    el.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const visibleStartRow = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT));
  const visibleRowCount = Math.ceil((viewportHeight || 0) / ROW_HEIGHT) + VIRTUAL_BUFFER;
  const visibleEndRow = Math.min(TOTAL_ROWS, visibleStartRow + visibleRowCount);
  const topPad = visibleStartRow * ROW_HEIGHT;
  const bottomPad = (TOTAL_ROWS - visibleEndRow) * ROW_HEIGHT;
  
  const { toast } = useToast();
  const { contextMenu, showContextMenu, hideContextMenu, submenuState, setSubmenuState } = useContextMenu();
  const [chartsOpen, setChartsOpen] = useState(false);
  const [chartTable, setChartTable] = useState<{ headers: string[]; rows: Record<string, string | number>[] } | null>(null);
  const [chartOverlays, setChartOverlays] = useState<ChartOverlayItem[]>([]);
  const [currentFilePath, setCurrentFilePath] = useState<string>('');
  const [autoSaving, setAutoSaving] = useState<boolean>(false);
  const [isSaved, setIsSaved] = useState<boolean>(false);
  const [showFileBrowser, setShowFileBrowser] = useState<boolean>(false);
  const [showSaveDialog, setShowSaveDialog] = useState<boolean>(false);
  const [saveFileName, setSaveFileName] = useState<string>('');
  const { user } = useAuth();
  const [osUsername, setOsUsername] = useState<string>('User');
  const [usernameReady, setUsernameReady] = useState<boolean>(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<typeof EXCEL_FUNCTIONS>([]);
  const [highlightIndex, setHighlightIndex] = useState<number>(0);
  const formulaInputRef = useRef<HTMLInputElement>(null);
  const cellEditorRef = useRef<HTMLInputElement>(null);
  const [activeEditor, setActiveEditor] = useState<'formulaBar' | 'cell' | null>(null);
  const [formulasOpen, setFormulasOpen] = useState<boolean>(false);
  const [sheetSelectionRange, setSheetSelectionRange] = useState<{
    start: { row: number; col: number };
    end: { row: number; col: number };
  } | null>(null);
  const [shortcutDialogOpen, setShortcutDialogOpen] = useState(false);
  const [shortcutDialogInitialMode, setShortcutDialogInitialMode] = useState<'current-sheet' | 'upload-files'>('current-sheet');
  const [shortcutStatusOpen, setShortcutStatusOpen] = useState(false);
  const [shortcutReviewOpen, setShortcutReviewOpen] = useState(false);
  const [shortcutHistoryOpen, setShortcutHistoryOpen] = useState(false);
  const [shortcutConnectionVerified, setShortcutConnectionVerified] = useState(false);
  const [shortcutSubmitting, setShortcutSubmitting] = useState(false);
  const [shortcutHistoryRefreshing, setShortcutHistoryRefreshing] = useState(false);
  const [shortcutRuns, setShortcutRuns] = useState<ShortcutRunRecord[]>([]);
  const [activeShortcutRunId, setActiveShortcutRunId] = useState<string | null>(null);
  const [shortcutReviewDataByRunId, setShortcutReviewDataByRunId] = useState<Record<string, ShortcutRunReviewData>>({});
  const shortcutPollTimersRef = useRef<Record<string, number>>({});
  const shortcutPollAttemptsRef = useRef<Record<string, number>>({});

  const nonEmptyCells = React.useMemo(() => {
    let count = 0;
    for (const key in data) {
      const cell = data[key];
      if (!cell) continue;
      const raw = (cell.formula ?? cell.value ?? '').trim();
      if (raw.length > 0) count++;
    }
    return count;
  }, [data]);

  const [isSelectingRange, setIsSelectingRange] = useState<boolean>(false);
  const [selectionStart, setSelectionStart] = useState<{ row: number; col: number } | null>(null);
  const [selectionEnd, setSelectionEnd] = useState<{ row: number; col: number } | null>(null);
  const [caretIndex, setCaretIndex] = useState<number>(0);
  const [isShiftDown, setIsShiftDown] = useState<boolean>(false);

  const startKeyboardRangeSelection = () => {
    if (!isFormulaMode()) return;
    updateCaretFromInput();

    if (!selectionStart || !selectionEnd) {
      const { row, col } = cellIdToPosition(selectedCell);
      setSelectionStart({ row, col });
      setSelectionEnd({ row, col });
    }
    setIsSelectingRange(true);
  };

  const moveSelectionEndByArrow = (key: 'ArrowDown' | 'ArrowUp' | 'ArrowLeft' | 'ArrowRight') => {
    setSelectionEnd((prev) => {
      const anchor = selectionStart || cellIdToPosition(selectedCell);
      const current = prev || anchor;
      let row = current.row;
      let col = current.col;
      if (key === 'ArrowDown') row = Math.min(TOTAL_ROWS - 1, row + 1);
      if (key === 'ArrowUp') row = Math.max(0, row - 1);
      if (key === 'ArrowRight') col = Math.min(columnCount - 1, col + 1);
      if (key === 'ArrowLeft') col = Math.max(0, col - 1);
      return { row, col };
    });
  };

  const updateCaretFromInput = () => {
    const el = activeEditor === 'formulaBar' ? formulaInputRef.current : cellEditorRef.current;
    if (!el) return;
    setCaretIndex(el.selectionStart ?? String(formulaInput ?? '').length);
  };

  const activeShortcutRun = React.useMemo(
    () => shortcutRuns.find((run) => run.shortcut_run_id === activeShortcutRunId) || null,
    [activeShortcutRunId, shortcutRuns]
  );

  const activeShortcutReviewData = activeShortcutRunId ? shortcutReviewDataByRunId[activeShortcutRunId] || null : null;

  const shortcutSourceFileName = React.useMemo(() => {
    return currentFilePath.split('\\').pop() || `${sheetName || 'Untitled Spreadsheet'}.msheet`;
  }, [currentFilePath, sheetName]);

  const shortcutSelectedRange = React.useMemo(() => {
    if (!sheetSelectionRange) {
      return null;
    }

    return rangeBoundsToLabel({
      startRow: Math.min(sheetSelectionRange.start.row, sheetSelectionRange.end.row),
      endRow: Math.max(sheetSelectionRange.start.row, sheetSelectionRange.end.row),
      startCol: Math.min(sheetSelectionRange.start.col, sheetSelectionRange.end.col),
      endCol: Math.max(sheetSelectionRange.start.col, sheetSelectionRange.end.col),
    });
  }, [sheetSelectionRange]);

  const shortcutSelectionSummary = shortcutSelectedRange || selectedCell;

  const getCurrentSheetComparisonTable = (range?: string | null) => {
    return buildMapleSheetCsvExport({
      data,
      getCellValue,
      selectedRange: range,
      fallbackCell: selectedCell,
    }).table;
  };

  const mergeShortcutRun = (nextRun: ShortcutRunRecord) => {
    setShortcutRuns((prev) => {
      const existingIndex = prev.findIndex((run) => run.id === nextRun.id || run.shortcut_run_id === nextRun.shortcut_run_id);
      if (existingIndex === -1) {
        return [nextRun, ...prev].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      }

      const updated = [...prev];
      updated[existingIndex] = nextRun;
      return updated.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    });
  };

  const loadShortcutHistory = async () => {
    setShortcutHistoryRefreshing(true);
    try {
      const runs = await listShortcutRunHistory();
      setShortcutRuns(runs);
    } catch (error) {
      toast({ title: 'Shortcut history unavailable', description: error instanceof Error ? error.message : 'Could not load Shortcut history.', variant: 'destructive' });
    } finally {
      setShortcutHistoryRefreshing(false);
    }
  };

  const openShortcutReviewForRun = async (run: ShortcutRunRecord) => {
    try {
      let reviewData = shortcutReviewDataByRunId[run.shortcut_run_id];
      if ((!reviewData || !reviewData.preview) && run.result_download_available) {
        reviewData = await parseShortcutReviewData({
          runId: run.shortcut_run_id,
          currentTable: getCurrentSheetComparisonTable(run.source_type === 'msheet' ? run.selected_range : null),
        });

        setShortcutReviewDataByRunId((prev) => ({
          ...prev,
          [run.shortcut_run_id]: reviewData,
        }));
      }

      setActiveShortcutRunId(run.shortcut_run_id);
      setShortcutStatusOpen(false);
      setShortcutReviewOpen(true);
    } catch (error) {
      toast({ title: 'Shortcut review unavailable', description: error instanceof Error ? error.message : 'Could not load the Shortcut result.', variant: 'destructive' });
    }
  };

  const saveShortcutDocument = async (fileName: string, content: string) => {
    const docs = await resolveDocumentsPath();
    const targetDir = `${docs}\\MapleSheet\\Shortcut`;
    await ensureDir(targetDir);
    const safeFileName = sanitizeFileName(fileName).replace(/\.(csv|xlsx|xlsm|msheet)$/i, '') || 'Shortcut Result';
    const filePath = `${targetDir}\\${safeFileName}.msheet`;
    await globalVFS.save(filePath, content);
    window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: targetDir } }));
    if (user) {
      await saveToSupabase(filePath, targetDir, content, 'application/x-maplesheet');
    }
    window.dispatchEvent(new CustomEvent('operator:openWindow', {
      detail: {
        appId: 'maple-sheet',
        title: `MapleSheet - ${safeFileName}.msheet`,
        filePath,
        fileContent: content,
      },
    }));
    return filePath;
  };

  const replaceSheetWithShortcutResult = async () => {
    if (!activeShortcutRun || !activeShortcutReviewData?.preview) {
      return;
    }

    const nextData = tableToSpreadsheetData(activeShortcutReviewData.preview.table) as SpreadsheetData;
    setData(nextData);
    setColumnCount(Math.max(DEFAULT_COLUMN_COUNT, getMaxUsedColumnIndex(nextData) + 1));
    setChartOverlays([]);
    setSelectedCell('A1');
    setFormulaInput('');
    setSheetSelectionRange(null);
    setIsSaved(false);
    const updatedRun = await markShortcutRunApplied(activeShortcutRun.id, 'replace');
    mergeShortcutRun(updatedRun);
    setShortcutReviewOpen(false);
    toast({ title: 'Shortcut applied', description: 'The current sheet was replaced with the reviewed Shortcut result.' });
  };

  const insertShortcutResultAsNewDocument = async () => {
    if (!activeShortcutRun || !activeShortcutReviewData?.preview) {
      return;
    }

    const nextName = (activeShortcutReviewData.file.fileName || `${sheetName}-shortcut`).replace(/\.(csv|xlsx|xlsm)$/i, '');
    const content = buildMapleSheetDocumentContent(nextName, activeShortcutReviewData.preview.table);
    await saveShortcutDocument(nextName, content);
    const updatedRun = await markShortcutRunApplied(activeShortcutRun.id, 'new_document');
    mergeShortcutRun(updatedRun);
    setShortcutReviewOpen(false);
    toast({ title: 'Shortcut document created', description: 'A new MapleSheet document was created from the Shortcut result.' });
  };

  const downloadShortcutRunResult = async (run: ShortcutRunRecord) => {
    try {
      const file = shortcutReviewDataByRunId[run.shortcut_run_id]?.file || await downloadShortcutResult(run.shortcut_run_id);
      if (!shortcutReviewDataByRunId[run.shortcut_run_id]) {
        setShortcutReviewDataByRunId((prev) => ({
          ...prev,
          [run.shortcut_run_id]: {
            file,
            preview: null,
            diff: null,
          },
        }));
      }
      saveShortcutFile(file);
      const updatedRun = await markShortcutRunApplied(run.id, 'download');
      mergeShortcutRun(updatedRun);
    } catch (error) {
      toast({ title: 'Shortcut download failed', description: error instanceof Error ? error.message : 'Could not download the Shortcut result.', variant: 'destructive' });
    }
  };

  const downloadShortcutRunArtifact = async (run: ShortcutRunRecord, filename: string) => {
    try {
      const file = await downloadShortcutArtifact(run.shortcut_run_id, filename);
      saveShortcutFile(file);
    } catch (error) {
      toast({ title: 'Artifact download failed', description: error instanceof Error ? error.message : 'Could not download the Shortcut artifact.', variant: 'destructive' });
    }
  };

  const handleShortcutVerifyConnection = async () => {
    try {
      await verifyShortcutConnection();
      setShortcutConnectionVerified(true);
      toast({ title: 'Shortcut verified', description: 'Your server-side Shortcut connection is ready.' });
    } catch (error) {
      setShortcutConnectionVerified(false);
      toast({ title: 'Shortcut verification failed', description: error instanceof Error ? error.message : 'Could not verify Shortcut access.', variant: 'destructive' });
    }
  };

  const handleShortcutSubmit = async (submission: ShortcutRunDialogSubmission) => {
    if (!user?.id) {
      toast({ title: 'Sign in required', description: 'Please sign in to use Shortcut history and background tracking.', variant: 'destructive' });
      return;
    }

    setShortcutSubmitting(true);
    try {
      let initFile: string | undefined;
      let contextFiles: string[] | undefined;
      let sourceType = getShortcutSourceTypeFromName(shortcutSourceFileName, submission.mode === 'current-sheet');
      let sourceFileName = shortcutSourceFileName;
      let sourceFilePath = currentFilePath || null;
      let selectedRange = shortcutSelectedRange || selectedCell;
      let prompt = submission.prompt;

      if (submission.mode === 'current-sheet') {
        const currentSheetExport = buildMapleSheetWorkbookExport({
          data,
          getCellValue,
          selectedRange: shortcutSelectedRange,
          fallbackCell: selectedCell,
          sheetName,
        });
        const workbookFile = new File(
          [currentSheetExport.workbookBlob],
          `${sheetName || 'MapleSheet'}.xlsx`,
          { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
        );
        const uploaded = await uploadShortcutFile(workbookFile);
        initFile = uploaded.fileId;
        sourceType = 'msheet';
        sourceFileName = shortcutSourceFileName;
        sourceFilePath = currentFilePath || null;
        selectedRange = shortcutSelectedRange || currentSheetExport.exportedRange || selectedCell;
        prompt = buildShortcutPrompt({
          userPrompt: submission.prompt,
          selectedRange: shortcutSelectedRange,
          activeCell: selectedCell,
          sourceLabel: `${sourceFileName} exported from MapleSheet as XLSX`,
        });
      } else {
        const uploadedFiles = await Promise.all(submission.files.slice(0, 5).map((file) => uploadShortcutFile(file)));
        initFile = uploadedFiles[0]?.fileId;
        contextFiles = uploadedFiles.slice(1).map((item) => item.fileId);
        sourceFileName = submission.files[0]?.name || 'Uploaded workbook';
        sourceFilePath = null;
        sourceType = getShortcutSourceTypeFromName(sourceFileName, false);
        selectedRange = null;
      }

      const remoteRun = await submitShortcutJob({
        prompt,
        initFile,
        contextFiles,
        timeoutMs: submission.timeoutMs,
        mode: submission.agentMode,
        skills: submission.skills,
      });

      const localRun = await createShortcutRunRecord({
        user_id: user?.id || '',
        shortcut_run_id: remoteRun.runId,
        source_file_path: sourceFilePath,
        source_file_name: sourceFileName,
        source_type: sourceType,
        selected_range: selectedRange,
        prompt: submission.prompt,
        status: remoteRun.status,
        mode: submission.agentMode,
        timeout_ms: submission.timeoutMs ?? null,
        skills: submission.skills,
        applied_action: 'none',
      });

      mergeShortcutRun(localRun);
      setActiveShortcutRunId(localRun.shortcut_run_id);
      setShortcutDialogOpen(false);
      setShortcutHistoryOpen(false);
      setShortcutStatusOpen(true);
      toast({ title: 'Shortcut run submitted', description: `Run ${localRun.shortcut_run_id} is now ${localRun.status}.` });
    } catch (error) {
      toast({ title: 'Shortcut submission failed', description: error instanceof Error ? error.message : 'Could not submit the Shortcut job.', variant: 'destructive' });
    } finally {
      setShortcutSubmitting(false);
    }
  };

  const cellIdToPosition = (id: string): { row: number; col: number } => {
    const match = id.match(/^([A-Z]+)(\d+)$/i);
    if (!match) return { row: 0, col: 0 };
    const letters = match[1].toUpperCase();
    const row = Math.max(0, parseInt(match[2], 10) - 1);
    let col = 0;
    for (let i = 0; i < letters.length; i++) {
      col = col * 26 + (letters.charCodeAt(i) - 64);
    }
    return { row, col: Math.max(0, col - 1) };
  };

  const positionToCellId = (row: number, col: number): string => getCellId(row, col);

  const getMaxUsedColumnIndex = (sheetData: SpreadsheetData = data) => {
    let max = DEFAULT_COLUMN_COUNT - 1;
    for (const cellId of Object.keys(sheetData)) {
      const parsed = cellIdToPosition(cellId);
      max = Math.max(max, parsed.col);
    }
    return max;
  };

  const syncSelectionAfterDataChange = (nextData: SpreadsheetData, nextCellId: string) => {
    setData(nextData);
    setSelectedCell(nextCellId);
    setFormulaInput(nextData[nextCellId]?.formula || nextData[nextCellId]?.value || '');
    setSheetSelectionRange(null);
    setIsSaved(false);
  };

  const insertRowAt = (rowIndex: number) => {
    const nextData: SpreadsheetData = {};
    for (const [cellId, cell] of Object.entries(data)) {
      const { row, col } = cellIdToPosition(cellId);
      const nextRow = row >= rowIndex ? row + 1 : row;
      nextData[getCellId(nextRow, col)] = cell;
    }
    syncSelectionAfterDataChange(nextData, getCellId(rowIndex, 0));
  };

  const deleteRowAt = (rowIndex: number) => {
    const nextData: SpreadsheetData = {};
    for (const [cellId, cell] of Object.entries(data)) {
      const { row, col } = cellIdToPosition(cellId);
      if (row === rowIndex) continue;
      const nextRow = row > rowIndex ? row - 1 : row;
      nextData[getCellId(nextRow, col)] = cell;
    }
    syncSelectionAfterDataChange(nextData, getCellId(Math.max(0, rowIndex - 1), 0));
  };

  const insertColumnAt = (colIndex: number) => {
    const nextData: SpreadsheetData = {};
    for (const [cellId, cell] of Object.entries(data)) {
      const { row, col } = cellIdToPosition(cellId);
      const nextCol = col >= colIndex ? col + 1 : col;
      nextData[getCellId(row, nextCol)] = cell;
    }
    setColumnCount((prev) => Math.max(prev + 1, getMaxUsedColumnIndex(nextData) + 1));
    syncSelectionAfterDataChange(nextData, getCellId(0, colIndex));
  };

  const deleteColumnAt = (colIndex: number) => {
    const nextData: SpreadsheetData = {};
    for (const [cellId, cell] of Object.entries(data)) {
      const { row, col } = cellIdToPosition(cellId);
      if (col === colIndex) continue;
      const nextCol = col > colIndex ? col - 1 : col;
      nextData[getCellId(row, nextCol)] = cell;
    }
    setColumnCount(Math.max(1, getMaxUsedColumnIndex(nextData) + 1));
    syncSelectionAfterDataChange(nextData, getCellId(0, Math.max(0, colIndex - 1)));
  };

  const openRowHeaderContextMenu = (event: React.MouseEvent, row: number) => {
    const items: ContextMenuItem[] = [
      {
        id: `insert-row-above-${row}`,
        label: 'Insert row above',
        icon: <Plus className="h-4 w-4" />,
        onClick: () => insertRowAt(row),
      },
      {
        id: `insert-row-below-${row}`,
        label: 'Insert row below',
        icon: <Plus className="h-4 w-4" />,
        onClick: () => insertRowAt(row + 1),
      },
      { id: `separator-row-${row}`, label: '', separator: true },
      {
        id: `delete-row-${row}`,
        label: 'Delete row',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: () => deleteRowAt(row),
      },
    ];
    setSelectedCell(getCellId(row, 0));
    setFormulaInput(data[getCellId(row, 0)]?.formula || data[getCellId(row, 0)]?.value || '');
    showContextMenu(event, items);
  };

  const openColumnHeaderContextMenu = (event: React.MouseEvent, col: number) => {
    const items: ContextMenuItem[] = [
      {
        id: `insert-column-left-${col}`,
        label: 'Insert column left',
        icon: <Plus className="h-4 w-4" />,
        onClick: () => insertColumnAt(col),
      },
      {
        id: `insert-column-right-${col}`,
        label: 'Insert column right',
        icon: <Plus className="h-4 w-4" />,
        onClick: () => insertColumnAt(col + 1),
      },
      { id: `separator-column-${col}`, label: '', separator: true },
      {
        id: `delete-column-${col}`,
        label: 'Delete column',
        icon: <Trash2 className="h-4 w-4" />,
        onClick: () => deleteColumnAt(col),
      },
    ];
    setSelectedCell(getCellId(0, col));
    setFormulaInput(data[getCellId(0, col)]?.formula || data[getCellId(0, col)]?.value || '');
    showContextMenu(event, items);
  };

  const getRangeString = (start: { row: number; col: number }, end: { row: number; col: number }) => {
    const startId = positionToCellId(start.row, start.col);
    const endId = positionToCellId(end.row, end.col);
    return startId === endId ? startId : `${startId}:${endId}`;
  };

  const replaceRangeAtCaret = (replacement: string) => {
    const value = formulaInput || '';
    const before = value.slice(0, caretIndex);
    const after = value.slice(caretIndex);
    const newValue = `${before}${replacement}${after}`;
    setFormulaInput(newValue);
    setTimeout(() => {
      const pos = before.length + replacement.length;
      const el = activeEditor === 'formulaBar' ? formulaInputRef.current : cellEditorRef.current;
      el?.focus();
      el?.setSelectionRange(pos, pos);
      setCaretIndex(pos);
    }, 0);
  };

  const isFormulaMode = () => String(formulaInput ?? '').startsWith('=');

  const finalizeRangeSelection = () => {
    if (!isSelectingRange || !selectionStart || !selectionEnd) return;
    const range = getRangeString(selectionStart, selectionEnd);
    replaceRangeAtCaret(range);
    setIsSelectingRange(false);
  };

  const beginRangeSelection = (row: number, col: number) => {
    setIsSelectingRange(true);
    setSelectionStart({ row, col });
    setSelectionEnd({ row, col });
  };

  const updateRangeSelection = (row: number, col: number) => {
    if (!isSelectingRange) return;
    setSelectionEnd({ row, col });
  };

  const isCellInSelection = (row: number, col: number): boolean => {
    if (isSelectingRange && selectionStart && selectionEnd) {
      const minRow = Math.min(selectionStart.row, selectionEnd.row);
      const maxRow = Math.max(selectionStart.row, selectionEnd.row);
      const minCol = Math.min(selectionStart.col, selectionEnd.col);
      const maxCol = Math.max(selectionStart.col, selectionEnd.col);
      if (row >= minRow && row <= maxRow && col >= minCol && col <= maxCol) {
        return true;
      }
    }

    if (sheetSelectionRange) {
      const minRow = Math.min(sheetSelectionRange.start.row, sheetSelectionRange.end.row);
      const maxRow = Math.max(sheetSelectionRange.start.row, sheetSelectionRange.end.row);
      const minCol = Math.min(sheetSelectionRange.start.col, sheetSelectionRange.end.col);
      const maxCol = Math.max(sheetSelectionRange.start.col, sheetSelectionRange.end.col);
      return row >= minRow && row <= maxRow && col >= minCol && col <= maxCol;
    }

    return false;
  };

  const isFormulaComplete = (value: string) => {
    const text = String(value ?? '');
    let depth = 0;
    for (const char of text) {
      if (char === '(') depth += 1;
      if (char === ')') depth -= 1;
      if (depth < 0) return true;
    }
    return depth <= 0;
  };

  const handleCellSelect = (cellId: string, options?: { preserveRange?: boolean }) => {
    setSelectedCell(cellId);
    if (!options?.preserveRange) {
      setSheetSelectionRange(null);
    }
    const cell = data[cellId];
    const raw = cell?.formula || cell?.value || '';
    setFormulaInput(raw);
    updateSuggestions(raw);
  };

  const handleCellChange = (cellId: string, value: string) => {
    setData((prev) => ({
      ...prev,
      [cellId]: {
        ...prev[cellId],
        value,
        formula: value.startsWith('=') ? value : undefined,
      },
    }));
    setIsSaved(false);
  };

  const getCellValue = (cellId: string): string => {
    const cell = data[cellId];
    if (!cell) return '';
    if (cell.formula?.startsWith('=')) {
      try {
        return String(evaluateExcelFormula(cell.formula, (refCellId) => {
          const refCell = data[refCellId];
          return refCell?.formula
            ? evaluateExcelFormula(refCell.formula, (nestedCellId) => data[nestedCellId]?.value || '')
            : refCell?.value || '';
        }));
      } catch {
        return '#ERROR!';
      }
    }
    return cell.value || '';
  };

  const applyTableAt = (startCell: string, table: string[][]) => {
    const { row: startRow, col: startCol } = cellIdToPosition(startCell);
    const next = { ...data };
    for (let r = 0; r < table.length; r++) {
      for (let c = 0; c < table[r].length; c++) {
        const cellId = getCellId(startRow + r, startCol + c);
        const value = String(table[r][c] ?? '');
        next[cellId] = {
          ...(next[cellId] || {}),
          value,
          formula: value.startsWith('=') ? value : undefined,
        };
      }
    }
    setData(next as SpreadsheetData);
    setColumnCount(Math.max(DEFAULT_COLUMN_COUNT, getMaxUsedColumnIndex(next as SpreadsheetData) + 1));
    setIsSaved(false);
  };

  const buildContentJSON = () => JSON.stringify({ name: sheetName, data, charts: chartOverlays }, null, 2);

  useEffect(() => {
    const init = async () => {
      try {
        const session = await supabase.auth.getSession();
        const sessionUser = session.data.session?.user;
        if (sessionUser) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('username')
            .eq('id', sessionUser.id)
            .maybeSingle();
          const profileData = (profile as { username?: string | null } | null) ?? null;
          const uname = (profileData?.username || sessionUser.email?.split('@')[0] || 'User').replace(/[^a-zA-Z0-9]/g, '') || 'User';
          setOsUsername(uname);
        }
      } finally {
        setUsernameReady(true);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (filePath) setCurrentFilePath(filePath);
    if (!initialContent) return;
    try {
      const parsed = JSON.parse(initialContent);
      const nextData = (parsed?.data || {}) as SpreadsheetData;
      setData(nextData);
      setColumnCount(Math.max(DEFAULT_COLUMN_COUNT, getMaxUsedColumnIndex(nextData) + 1));
      setSheetName(parsed?.name || 'Untitled Spreadsheet');
      setChartOverlays(Array.isArray(parsed?.charts) ? parsed.charts : []);
      setIsSaved(true);
    } catch {
      const nextData = { A1: { value: initialContent } } as SpreadsheetData;
      setData(nextData);
      setColumnCount(DEFAULT_COLUMN_COUNT);
      setSheetName('Imported Spreadsheet');
    }
  }, [filePath, initialContent]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftDown(true);
        if (isFormulaMode()) {
          startKeyboardRangeSelection();
        }
      }
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        setIsShiftDown(false);
        if (isSelectingRange) {
          finalizeRangeSelection();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [isSelectingRange, formulaInput, selectionStart, selectionEnd, caretIndex, activeEditor]);

  // Auto-save draft shortly after edits if no explicit file path yet (uses per-user Documents path)
  useEffect(() => {
    if (currentFilePath) return;
    const hasAnyData = Object.keys(data).length > 0 || chartOverlays.length > 0;
    if (!hasAnyData || !usernameReady) return;
    const timer = setTimeout(async () => {
      const docs = await resolveDocumentsPath();
      const draftsPath = `${docs}\\MapleSheet\\Drafts`;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filePath = `${draftsPath}\\Untitled-${timestamp}.msheet`;
      await saveToVFSAndSupabase(filePath, draftsPath, { silent: true });
    }, 1000);
    return () => clearTimeout(timer);
  }, [data, chartOverlays, currentFilePath, usernameReady, osUsername]);

  useEffect(() => {
    if (!user?.id) {
      setShortcutRuns([]);
      return;
    }
    loadShortcutHistory();
  }, [user?.id]);

  useEffect(() => {
    const activeRuns = shortcutRuns.filter((run) => !isShortcutTerminalStatus(run.status));

    activeRuns.forEach((run) => {
      if (shortcutPollTimersRef.current[run.shortcut_run_id]) {
        return;
      }

      const attempt = shortcutPollAttemptsRef.current[run.shortcut_run_id] || 0;
      const delay = Math.min(15000, 3000 + attempt * 2000);
      shortcutPollTimersRef.current[run.shortcut_run_id] = window.setTimeout(async () => {
        delete shortcutPollTimersRef.current[run.shortcut_run_id];
        shortcutPollAttemptsRef.current[run.shortcut_run_id] = attempt + 1;

        const result = await syncShortcutRunStatus(
          run,
          getCurrentSheetComparisonTable(run.source_type === 'msheet' ? run.selected_range : null)
        );

        mergeShortcutRun(result.run);

        if (result.reviewData) {
          setShortcutReviewDataByRunId((prev) => ({
            ...prev,
            [run.shortcut_run_id]: result.reviewData,
          }));
          shortcutPollAttemptsRef.current[run.shortcut_run_id] = 0;
          toast({ title: 'Shortcut run completed', description: `${result.run.source_file_name || 'Shortcut job'} is ready for review.` });
          if (activeShortcutRunId === run.shortcut_run_id || shortcutStatusOpen) {
            setActiveShortcutRunId(run.shortcut_run_id);
            setShortcutStatusOpen(false);
            setShortcutReviewOpen(true);
          }
        } else if (result.run.status === 'failed' || result.run.status === 'error') {
          toast({ title: 'Shortcut run failed', description: result.run.error_message || 'Shortcut could not complete this job.', variant: 'destructive' });
        }
      }, delay);
    });

    return () => {
      shortcutRuns
        .filter((run) => isShortcutTerminalStatus(run.status))
        .forEach((run) => {
          const timer = shortcutPollTimersRef.current[run.shortcut_run_id];
          if (timer) {
            window.clearTimeout(timer);
            delete shortcutPollTimersRef.current[run.shortcut_run_id];
          }
        });
    };
  }, [activeShortcutRunId, shortcutRuns, shortcutStatusOpen]);

  useEffect(() => {
    return () => {
      Object.values(shortcutPollTimersRef.current).forEach((timer) => window.clearTimeout(timer));
      shortcutPollTimersRef.current = {};
    };
  }, []);

  const handleNewSheet = () => {
    setData({} as SpreadsheetData);
    setColumnCount(DEFAULT_COLUMN_COUNT);
    setSheetName('Untitled Spreadsheet');
    setSelectedCell('A1');
    setFormulaInput('');
    setChartOverlays([]);
    setIsSaved(false);
    
    toast({
      title: "New Spreadsheet",
      description: "Started a new spreadsheet.",
    });
  };

  const insertRow = () => {
    const { row } = cellIdToPosition(selectedCell);
    insertRowAt(row + 1);
    toast({
      title: 'Row inserted',
      description: `Inserted a new row below ${row + 1}.`,
    });
  };

  const insertColumn = () => {
    const { col } = cellIdToPosition(selectedCell);
    insertColumnAt(col + 1);
    toast({
      title: 'Column inserted',
      description: `Inserted a new column to the right of ${columnIndexToLetter(col)}.`,
    });
  };

  const getMaxRowUsed = () => {
    let max = 1;
    for (const id in data) {
      const cell = data[id];
      if (!cell) continue;
      const raw = (cell.formula ?? cell.value ?? '').trim();
      if (!raw) continue;
      const m = id.match(/^[A-Za-z]+(\d+)$/);
      if (m) {
        const r = Math.max(1, parseInt(m[1], 10));
        if (r > max) max = r;
      }
    }
    return max;
  };

  const buildChartTable = () => {
    const headers: string[] = Array.from({ length: columnCount }, (_, col) => {
      const h = getCellValue(getCellId(0, col)).trim();
      return h || columnIndexToLetter(col);
    });
    const rows: Record<string, string | number>[] = [];
    const maxRow = Math.min(getMaxRowUsed(), 10000);
    for (let row = 1; row < maxRow; row++) {
      const obj: Record<string, string | number> = {};
      let hasValue = false;
      for (let col = 0; col < columnCount; col++) {
        const cellId = getCellId(row, col);
        const raw = getCellValue(cellId).trim();
        if (raw !== "") {
          const num = Number(raw);
          obj[headers[col]] = !isNaN(num) ? num : raw;
          hasValue = true;
        }
      }
      if (hasValue) rows.push(obj);
    }
    return { headers, rows };
  };

  const handleExportCSV = () => {
    const csvEscape = (val: string) => {
      const text = val ?? "";
      return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    try {
      const lines: string[] = [];
      const maxRow = getMaxRowUsed();
      for (let row = 0; row < maxRow; row++) {
        const cols: string[] = [];
        for (let col = 0; col < columnCount; col++) {
          const cellId = getCellId(row, col);
          const value = getCellValue(cellId);
          cols.push(csvEscape(value));
        }
        lines.push(cols.join(","));
      }
      const csv = lines.join("\r\n");
      const base = sheetName.trim() || "Untitled Spreadsheet";
      const fileName = /\.csv$/i.test(base) ? base : `${base}.csv`;
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      saveAs(blob, fileName);
      toast({ title: "CSV Exported", description: `Downloaded ${fileName}` });
    } catch {
      toast({ title: "Export Failed", description: "Could not export CSV.", variant: "destructive" });
    }
  };

  const handleCreateChart = (payload: { imageSrc: string; config: ChartOverlayConfig }) => {
    const id = `chart_${Date.now()}`;
    const offsetX = 120;
    const offsetY = 80;
    setChartOverlays((prev) => [
      ...prev,
      { id, src: payload.imageSrc, config: payload.config, x: offsetX, y: offsetY, width: 360 },
    ]);
    toast({
      title: "Chart inserted",
      description: "A chart has been added to your sheet. Drag to move and use corners to resize.",
    });
  };

  const moveChart = (id: string, x: number, y: number) => {
    setChartOverlays((prev) => prev.map((c) => (c.id === id ? { ...c, x, y } : c)));
  };

  const resizeChart = (id: string, x: number, y: number, width: number) => {
    setChartOverlays((prev) => prev.map((c) => (c.id === id ? { ...c, x, y, width } : c)));
  };

  const updateSuggestions = (value: string) => {
    const val = String(value ?? '');
    if (!val.startsWith('=')) {
      setSuggestionsOpen(false);
      setSuggestions([]);
      setHighlightIndex(0);
      return;
    }
    const afterEq = val.slice(1);
    if (afterEq.includes('(')) {
      setSuggestionsOpen(false);
      return;
    }
    const match = afterEq.match(/^[A-Za-z]+/);
    const token = (match ? match[0] : '').toUpperCase();
    const filtered = EXCEL_FUNCTIONS
      .filter(fn => token.length === 0 || fn.name.startsWith(token))
      .slice(0, 20);
    setSuggestions(filtered);
    setSuggestionsOpen(filtered.length > 0);
    setHighlightIndex(0);
  };

  const insertFunction = (fnName: string) => {
    const current = formulaInput;
    const afterEq = current.startsWith('=') ? current.slice(1) : '';
    const match = afterEq.match(/^([A-Za-z]*)(.*)$/);
    const rest = (match ? match[2] : '');
    const hasParen = /\(/.test(rest);
    const newValue = '=' + fnName + (hasParen ? rest : '()' + rest);
    setFormulaInput(newValue);
    setSuggestionsOpen(false);
    const caretPos = ('=' + fnName + '(').length;
    setTimeout(() => {
      const el = activeEditor === 'formulaBar' ? formulaInputRef.current : cellEditorRef.current;
      el?.focus();
      el?.setSelectionRange(caretPos, caretPos);
    }, 0);
  };

  const handleInsertFromLibrary = (name: string) => {
    if (activeEditor !== 'formulaBar') {
      setActiveEditor('formulaBar');
      setTimeout(() => {
        formulaInputRef.current?.focus();
        if (!String(formulaInput ?? '').startsWith('=')) {
          setFormulaInput('=');
          setTimeout(() => insertFunction(name), 0);
        } else {
          insertFunction(name);
        }
      }, 0);
    } else {
      insertFunction(name);
    }
  };

  // Helper: resolve Documents path for current user (same pattern as SaveAsDialog)
  const resolveDocumentsPath = async (): Promise<string> => {
    const { data } = await supabase.auth.getSession();
    const user = data.session?.user;
    let username = "User";
    if (user) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("username,email")
        .eq("id", user.id)
        .maybeSingle();
      const profileData = (profile as { username?: string | null; email?: string | null } | null) ?? null;
      const candidate =
        profileData?.username ||
        (profileData?.email || user.email || "User").split("@")[0] ||
        "User";

      username = (candidate.replace(/[^a-zA-Z0-9]/g, "") || "User");
    }
    return `C:\\User\\${username}\\Documents`;
  };

  const sanitizeFileName = (name: string) =>
    String(name ?? "")
      .replace(/[<>:"/\\|?*]+/g, "")
      .trim();

  const handleSave = async () => {
    if (currentFilePath) {
      const parent = currentFilePath.substring(0, currentFilePath.lastIndexOf("\\"));
      await saveToVFSAndSupabase(currentFilePath, parent);
    } else {
      const docs = await resolveDocumentsPath();
      const targetDir = `${docs}\\MapleSheet`;
      await ensureDir(targetDir);

      const base = sanitizeFileName(sheetName || "Untitled Spreadsheet");
      const stripped = base.replace(/\.(json|csv|msheet)$/i, "");
      const fileName = `${stripped}.msheet`;
      const filePath = `${targetDir}\\${fileName}`;

      await saveToVFSAndSupabase(filePath, targetDir);
    }
  };

  const handleSaveAs = () => {
    const base = sheetName.trim();
    const stripped = base.replace(/\.(json|csv|msheet)$/i, '');
    const next = stripped.toLowerCase().endsWith('.msheet') ? stripped : `${stripped}.msheet`;
    setSaveFileName(next || 'untitled.msheet');
    setShowSaveDialog(true);
  };

  const handleSaveLocation = async (selectedFolder: any) => {
    const rawName = saveFileName || `${sheetName}.msheet`;
    const fileName = rawName.toLowerCase().endsWith('.msheet')
      ? rawName
      : `${rawName.replace(/\.(json|csv)$/i, '')}.msheet`;
    const base = (selectedFolder?.path || selectedFolder?.file_path || 'C:\\').trim();
    const basePath = base.endsWith('\\') ? base : `${base}\\`;
    const filePath = `${basePath}${fileName}`;
    await saveToVFSAndSupabase(filePath, basePath.slice(0, -1));
    setShowSaveDialog(false);
  };

  const saveToVFSAndSupabase = async (path: string, parentPath?: string, opts?: { silent?: boolean }) => {
    if (!opts?.silent) setIsSaving(true);
    try {
      const content = buildContentJSON();
      const dir = parentPath || path.substring(0, path.lastIndexOf('\\'));
      await ensureDir(dir);
      await globalVFS.save(path, content);
      window.dispatchEvent(new CustomEvent('fileSystemChanged', { detail: { path: dir } }));
      if (user) {
        await saveToSupabase(path, dir, content, 'application/x-maplesheet');
      }
      setCurrentFilePath(path);
      setIsSaved(true);
    } catch (error) {
      toast({
        title: 'Save Failed',
        description: 'Failed to save the spreadsheet. Please try again.',
        variant: 'destructive',
      });
    } finally {
      if (!opts?.silent) setIsSaving(false);
    }
  };

  const saveToSupabase = async (path: string, parentPath: string | undefined, content: string, contentType?: string) => {
    if (!user) return;
    try {
      const osUserFiles = () => supabase.from('os_user_files') as any;
      const fileName = path.split('\\').pop() || 'Untitled.msheet';
      const parent = parentPath || path.substring(0, path.lastIndexOf('\\'));
      const { data: existingFile } = await osUserFiles()
        .select('id')
        .eq('user_id', user.id)
        .eq('file_path', path)
        .maybeSingle();
      let result;
      if (existingFile) {
        result = await osUserFiles()
          .update({
            content,
            size_bytes: content.length,
            updated_at: new Date().toISOString(),
            ...(contentType ? { content_type: contentType } : {}),
          })
          .eq('id', existingFile.id)
          .eq('user_id', user.id);
      } else {
        result = await osUserFiles()
          .insert({
            user_id: user.id,
            file_path: path,
            file_name: fileName,
            parent_path: parent,
            content,
            content_type: contentType || 'application/json',
            size_bytes: content.length,
            is_directory: false,
          });
      }

      if ((result as any).error) throw (result as any).error;
    } catch (error) {
      console.error('[MapleSheet] saveToSupabase error:', error);
      toast({ title: 'Save Failed', description: 'Could not save to cloud.', variant: 'destructive' });
    }
  };

  const handleOpen = () => {
    setShowFileBrowser(true);
  };

  const handleFileSelect = async (file: any) => {
    setShowFileBrowser(false);
    if (!file) return;
    setIsLoading(true);
    try {
      const path = file.path || file.file_path;
      const name = file.file_name || path?.split('\\').pop() || 'Spreadsheet';
      const content = file.content ?? (await globalVFS.load(path));
      if (!content) throw new Error('No file content');
      const parsed = JSON.parse(content);
      const nextData = (parsed?.data || {}) as SpreadsheetData;

      setData(nextData);
      setColumnCount(Math.max(DEFAULT_COLUMN_COUNT, getMaxUsedColumnIndex(nextData) + 1));
      setSheetName(parsed?.name || name.replace(/\.msheet$/i, ''));

      setChartOverlays(Array.isArray(parsed?.charts) ? parsed.charts : []);
      setCurrentFilePath(path);
      setIsSaved(true);
      toast({ title: 'Spreadsheet opened', description: name });
    } catch {
      toast({ title: 'Open Failed', description: 'Could not open spreadsheet.', variant: 'destructive' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormulaSubmit = (navigate?: 'down' | 'up' | 'right' | 'left') => {
    handleCellChange(selectedCell, formulaInput);
    const { row, col } = cellIdToPosition(selectedCell);
    let nextRow = row;
    let nextCol = col;
    if (navigate === 'down') nextRow += 1;
    if (navigate === 'up') nextRow = Math.max(0, row - 1);
    if (navigate === 'right') nextCol = Math.min(columnCount - 1, col + 1);
    if (navigate === 'left') nextCol = Math.max(0, col - 1);
    const nextId = getCellId(nextRow, nextCol);
    setSelectedCell(nextId);
    setFormulaInput(data[nextId]?.formula || data[nextId]?.value || '');
    setSuggestionsOpen(false);
  };

  return (
    <div className="relative flex flex-col h-full bg-background" onClick={hideContextMenu}>
      {/* Header */}
      <div className="border-b border-border p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Input
              value={sheetName}
              onChange={(e) => { setSheetName(e.target.value); setIsSaved(false); }}
              className="max-w-64"
              placeholder="Spreadsheet name"
              onKeyDown={async (e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  await handleSave();
                  (e.target as HTMLInputElement).blur();
                }
              }}
            />
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Selected: {shortcutSelectionSummary}</span>
          </div>

        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={handleNewSheet} size="sm" variant="outline">
            <Sheet className="w-4 h-4 mr-1" />
            New
          </Button>
          <Button size="sm" variant="outline" disabled={isLoading} onClick={handleOpen}>
            <FolderOpen className="w-4 h-4 mr-1" />
            Open
          </Button>
          <Button onClick={handleSave} size="sm" variant="outline" disabled={isSaving}>
            <Save className="w-4 h-4 mr-1" />
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
          
          <div className="h-6 w-px bg-border mx-2" />
          
          <Button onClick={insertRow} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Insert Row
          </Button>
          <Button onClick={insertColumn} size="sm" variant="outline">
            <Plus className="w-4 h-4 mr-1" />
            Insert Column
          </Button>
          
          <div className="h-6 w-px bg-border mx-2" />
          
          <Button onClick={handleExportCSV} size="sm" variant="outline">
            <Download className="w-4 h-4 mr-1" />
            Export CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              const table = buildChartTable();
              setChartTable(table);
              setChartsOpen(true);
              toast({
                title: "Charts",
                description: "Configure a chart from your sheet data.",
              });
            }}
           >
             <BarChart3 className="w-4 h-4 mr-1" />
             Charts
           </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setFormulasOpen(true)}
          >
             <Calculator className="w-4 h-4 mr-1" />
             Functions
           </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline">
                <Sparkles className="w-4 h-4 mr-1" />
                Shortcut
                <ChevronDown className="ml-1 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-64">
              <DropdownMenuItem onClick={() => { setShortcutDialogInitialMode('current-sheet'); setShortcutDialogOpen(true); }}>
                <Sparkles className="mr-2 h-4 w-4" />
                Run on current sheet
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShortcutDialogInitialMode('upload-files'); setShortcutDialogOpen(true); }}>
                <Upload className="mr-2 h-4 w-4" />
                Upload workbook to Shortcut
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => { setShortcutHistoryOpen(true); loadShortcutHistory(); }}>
                <History className="mr-2 h-4 w-4" />
                Job history
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Formula Bar */}
        <div className="mt-4">
          <div className="relative flex items-center gap-2">
            <span className="text-sm font-medium min-w-12">{selectedCell}</span>
            <Input
              ref={formulaInputRef}
              onFocus={() => setActiveEditor('formulaBar')}
              value={formulaInput}
              onChange={(e) => {
                setFormulaInput(e.target.value);
                updateSuggestions(e.target.value);
                updateCaretFromInput();
              }}
              onClick={updateCaretFromInput}
              onKeyUp={updateCaretFromInput}
              onSelect={updateCaretFromInput}
              onBlur={() => setActiveEditor(null)}
              onKeyDown={(e) => {
                if (
                  isFormulaMode() &&
                  e.shiftKey &&
                  ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)
                ) {
                  e.preventDefault();
                  startKeyboardRangeSelection();
                  moveSelectionEndByArrow(e.key as 'ArrowDown' | 'ArrowUp' | 'ArrowLeft' | 'ArrowRight');
                  return;
                }

                if (suggestionsOpen) {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    setHighlightIndex((i) => Math.min(i + 1, (suggestions.length || 1) - 1));
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    setHighlightIndex((i) => Math.max(i - 1, 0));
                  } else if (e.key === 'Enter') {
                    if (isFormulaComplete(formulaInput) || suggestions.length === 0) {
                      e.preventDefault();
                      handleFormulaSubmit(e.shiftKey ? 'up' : 'down');
                      return;
                    } else {
                      e.preventDefault();
                      insertFunction(suggestions[highlightIndex]?.name || suggestions[0].name);
                      return;
                    }
                  } else if (e.key === 'Escape') {
                    setSuggestionsOpen(false);
                  } else if (e.key === 'Tab') {
                    e.preventDefault();
                    handleFormulaSubmit(e.shiftKey ? 'left' : 'right');
                  }
                } else {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleFormulaSubmit(e.shiftKey ? 'up' : 'down');
                  } else if (e.key === 'Tab') {
                    e.preventDefault();
                    handleFormulaSubmit(e.shiftKey ? 'left' : 'right');
                  } else if (e.key === 'ArrowDown') {
                    e.preventDefault();
                    handleFormulaSubmit('down');
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault();
                    handleFormulaSubmit('up');
                  } else if (e.key === 'ArrowRight') {
                    e.preventDefault();
                    handleFormulaSubmit('right');
                  } else if (e.key === 'ArrowLeft') {
                    e.preventDefault();
                    handleFormulaSubmit('left');
                  }
                }
              }}
              placeholder="Enter value or formula (e.g., =A1+B1, =SUM(A1:A10))"
              className="flex-1"
            />
            <Button onClick={() => handleFormulaSubmit()} size="sm">
              ✓
            </Button>

            {suggestionsOpen && suggestions.length > 0 && (
              <FormulaSuggestions
                items={suggestions}
                highlightIndex={highlightIndex}
                onSelect={(item) => insertFunction(item.name)}
              />
            )}
          </div>
        </div>
      </div>

      {/* Content area: single scroller + pinned status bar */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        <div className="flex-1 p-4 overflow-auto" ref={containerRef}>
          <Card
            className={sheetCardClassName}
            style={lightSheetCardStyle}
            onMouseUp={() => {
              finalizeRangeSelection();
            }}
          >
            <div className="min-w-full">
              <table className="maplesheet-grid-table w-full border-collapse" style={lightSheetTableStyle}>
                <thead className="sticky top-0 z-30">
                  <tr>
                    <th
                      className={sheetCornerCellClassName}
                      style={lightSheetHeaderStyle}
                    ></th>
                    {Array.from({ length: columnCount }, (_, col) => (
                      <th
                        key={col}
                        className={`${sheetHeaderCellClassName} cursor-context-menu hover:bg-muted/80`}
                        style={lightSheetHeaderStyle}
                        onContextMenu={(event) => openColumnHeaderContextMenu(event, col)}
                      >
                        {columnIndexToLetter(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {topPad > 0 && (
                    <tr aria-hidden>
                      <td colSpan={columnCount + 1} style={{ height: topPad }} />
                    </tr>
                  )}
                  {Array.from({ length: Math.max(0, visibleEndRow - visibleStartRow) }, (_, i) => {
                    const row = visibleStartRow + i;
                    return (
                    <tr key={row}>
                      <td
                        className={`${sheetRowHeaderClassName} cursor-context-menu hover:bg-muted/80`}
                        style={lightSheetRowHeaderStyle}
                        onContextMenu={(event) => openRowHeaderContextMenu(event, row)}
                      >
                        {row + 1}
                      </td>
                      {Array.from({ length: columnCount }, (_, col) => {
                        const cellId = getCellId(row, col);
                        const isSelected = selectedCell === cellId;
                        const inDragSelect = isCellInSelection(row, col);

                        const getDisplayValue = () => {
                          if (isSelected && (activeEditor === 'cell' || activeEditor === 'formulaBar')) {
                            return formulaInput;
                          }
                          return getCellValue(cellId);
                        };

                        return (
                          <td
                            key={col}
                            className={getGridCellClassName(isSelected, inDragSelect)}
                            style={getLightGridCellStyle(isSelected, inDragSelect)}
                            onMouseDown={(e) => {
                              if (isFormulaMode()) {
                                e.preventDefault();
                                e.stopPropagation();
                                beginRangeSelection(row, col);
                                return;
                              }

                              if (e.shiftKey) {
                                const anchor = cellIdToPosition(selectedCell);
                                setSheetSelectionRange({
                                  start: anchor,
                                  end: { row, col },
                                });
                                handleCellSelect(cellId, { preserveRange: true });
                                return;
                              }

                              handleCellSelect(cellId);
                            }}
                            onMouseEnter={() => {
                              if (isSelectingRange) {
                                updateRangeSelection(row, col);
                              }
                            }}
                            onClick={(e) => {
                              if (!isFormulaMode()) {
                                if (e.shiftKey) {
                                  const anchor = sheetSelectionRange?.start || cellIdToPosition(selectedCell);
                                  setSheetSelectionRange({
                                    start: anchor,
                                    end: { row, col },
                                  });
                                  handleCellSelect(cellId, { preserveRange: true });
                                  return;
                                }

                                handleCellSelect(cellId);
                                return;
                              }

                              if (e.shiftKey) {
                                updateRangeSelection(row, col);
                                finalizeRangeSelection();
                                return;
                              }
                              beginRangeSelection(row, col);
                            }}

                          >
                            <input
                              type="text"
                              ref={(el) => { if (isSelected) cellEditorRef.current = el as HTMLInputElement; }}
                              value={getDisplayValue()}
                              onFocus={() => {
                                setActiveEditor('cell');
                                handleCellSelect(cellId, {
                                  preserveRange: isShiftDown || (sheetSelectionRange ? isCellInSelection(row, col) : false),
                                });
                              }}

                              onChange={(e) => {
                                handleCellChange(cellId, e.target.value);
                                setFormulaInput(e.target.value);
                                updateSuggestions(e.target.value);
                                if (col >= columnCount - 1) {
                                  setColumnCount((prev) => Math.max(prev, col + 1));
                                }
                              }}
                              onClick={updateCaretFromInput}
                              onKeyUp={updateCaretFromInput}
                              onSelect={updateCaretFromInput}
                              onBlur={() => setActiveEditor(null)}
                              onKeyDown={(e) => {
                                if (
                                  isFormulaMode() &&
                                  e.shiftKey &&
                                  ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight'].includes(e.key)
                                ) {
                                  e.preventDefault();
                                  startKeyboardRangeSelection();
                                  moveSelectionEndByArrow(e.key as 'ArrowDown' | 'ArrowUp' | 'ArrowLeft' | 'ArrowRight');
                                  return;
                                }

                                if (suggestionsOpen) {
                                  if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    setHighlightIndex((i) => Math.min(i + 1, (suggestions.length || 1) - 1));
                                  } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    setHighlightIndex((i) => Math.max(i - 1, 0));
                                  } else if (e.key === 'Enter') {
                                    if (isFormulaComplete(formulaInput) || suggestions.length === 0) {
                                      e.preventDefault();
                                      handleFormulaSubmit(e.shiftKey ? 'up' : 'down');
                                      return;
                                    } else {
                                      e.preventDefault();
                                      insertFunction(suggestions[highlightIndex]?.name || suggestions[0].name);
                                      return;
                                    }
                                  } else if (e.key === 'Escape') {
                                    setSuggestionsOpen(false);
                                  } else if (e.key === 'Tab') {
                                    e.preventDefault();
                                    handleFormulaSubmit(e.shiftKey ? 'left' : 'right');
                                  }
                                } else {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    handleFormulaSubmit(e.shiftKey ? 'up' : 'down');
                                  } else if (e.key === 'Tab') {
                                    e.preventDefault();
                                    handleFormulaSubmit(e.shiftKey ? 'left' : 'right');
                                  } else if (e.key === 'ArrowDown') {
                                    e.preventDefault();
                                    handleFormulaSubmit('down');
                                  } else if (e.key === 'ArrowUp') {
                                    e.preventDefault();
                                    handleFormulaSubmit('up');
                                  } else if (e.key === 'ArrowRight') {
                                    e.preventDefault();
                                    handleFormulaSubmit('right');
                                  } else if (e.key === 'ArrowLeft') {
                                    e.preventDefault();
                                    handleFormulaSubmit('left');
                                  }
                                }
                              }}
                              className={`maplesheet-grid-input w-full h-full px-1 text-xs bg-transparent border-none outline-none text-foreground ${
                                isSelected ? 'caret-primary' : ''
                              }`}
                              readOnly={isSelectingRange}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  )})}
                  {bottomPad > 0 && (
                    <tr aria-hidden>
                      <td colSpan={columnCount + 1} style={{ height: bottomPad }} />
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {chartOverlays.map((item) => (
              <ChartOverlay
                key={item.id}
                item={item}
                table={buildChartTable()}
                onMove={moveChart}
                onResize={resizeChart}
              />
            ))}
          </Card>
        </div>
        <MapleSheetStatusBar
          selectedCell={selectedCell}
          sheetName={sheetName}
          currentFilePath={currentFilePath}
          nonEmptyCells={nonEmptyCells}
          isSaving={isSaving}
          autoSaving={autoSaving}
          isSaved={isSaved}
          onSave={handleSave}
        />
      </div>

      <MapleSheetAiToolbar
        selectedCell={selectedCell}
        getCellValue={getCellValue}
        setCellValue={(id, value) => handleCellChange(id, value)}
        applyTableAt={applyTableAt}
        openChartsConfigurator={() => {
          const table = buildChartTable();
          setChartTable(table);
          setChartsOpen(true);
          toast({ title: 'Charts', description: 'Configure a chart from your sheet data.' });
        }}
      />

      <ShortcutRunDialog
        open={shortcutDialogOpen}
        onOpenChange={setShortcutDialogOpen}
        initialMode={shortcutDialogInitialMode}
        selectionSummary={shortcutSelectionSummary}
        sourceFileName={shortcutSourceFileName}
        connectionVerified={shortcutConnectionVerified}
        isSubmitting={shortcutSubmitting}
        onVerifyConnection={handleShortcutVerifyConnection}
        onOpenHistory={() => {
          setShortcutHistoryOpen(true);
          loadShortcutHistory();
        }}
        onSubmit={handleShortcutSubmit}
      />
      <ShortcutRunStatus
        open={shortcutStatusOpen}
        onOpenChange={setShortcutStatusOpen}
        run={activeShortcutRun}
        onOpenHistory={() => {
          setShortcutHistoryOpen(true);
          loadShortcutHistory();
        }}
      />
      <ShortcutReviewDialog
        open={shortcutReviewOpen}
        onOpenChange={setShortcutReviewOpen}
        run={activeShortcutRun}
        reviewData={activeShortcutReviewData}
        onReplaceCurrentSheet={replaceSheetWithShortcutResult}
        onInsertNewDocument={insertShortcutResultAsNewDocument}
        onDownloadResult={() => {
          if (activeShortcutRun) {
            downloadShortcutRunResult(activeShortcutRun);
          }
        }}
        onDownloadArtifact={(filename) => {
          if (activeShortcutRun) {
            downloadShortcutRunArtifact(activeShortcutRun, filename);
          }
        }}

      />
      <ShortcutHistoryDialog
        open={shortcutHistoryOpen}
        onOpenChange={setShortcutHistoryOpen}
        runs={shortcutRuns}
        isRefreshing={shortcutHistoryRefreshing}
        onRefresh={loadShortcutHistory}
        onOpenReview={openShortcutReviewForRun}
        onDownloadResult={downloadShortcutRunResult}
        onDownloadArtifact={downloadShortcutRunArtifact}
      />
      <MapleSheetChartModal
        open={chartsOpen}
        onOpenChange={setChartsOpen}
        table={chartTable}
        sheetName={sheetName}
        onCreateChart={handleCreateChart}
      />
      <FormulaLibraryDialog
        open={formulasOpen}
        onOpenChange={setFormulasOpen}
        onInsert={handleInsertFromLibrary}
      />

      <ContextMenu
        items={contextMenu.items}
        x={contextMenu.x}
        y={contextMenu.y}
        onClose={hideContextMenu}
        isVisible={contextMenu.isVisible}
        submenuState={submenuState}
        setSubmenuState={setSubmenuState}
      />

      {/* File dialogs */}

      <DraggableFileDialog
        isOpen={showFileBrowser}
        onClose={() => setShowFileBrowser(false)}
        onFileSelect={handleFileSelect}
        mode="open"
        acceptedTypes={['application/x-maplesheet', 'application/json']}
        title="Open Spreadsheet"
      />
      <DraggableFileDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        mode="save"
        initialPath="C:\\"
        title="Save Spreadsheet"
        fileName={saveFileName}
        onFileNameChange={setSaveFileName}
        onSave={handleSaveLocation}
      />
    </div>
  );
};

const TOTAL_ROWS = 100000;
const ROW_HEIGHT = 32;
const VIRTUAL_BUFFER = 30;
const DEFAULT_COLUMN_COUNT = 10;

const getCellId = (row: number, col: number): string => {
  return `${columnIndexToLetter(col)}${row + 1}`;
};